import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import {
  flowsSelectors,
  stepsSelectors,
} from '@polyai/stream/store/reducers/flows';
import Input from '@polyai/ui/components/atoms/Input';
import { ConfirmationModal } from '@polyai/ui/components/molecules/ConfirmationModal';

import type { AddStepOnChange } from 'components/molecules/LexicalEditor/features/action/components/FlowTransition';
import { useAddStepFunction } from 'components/molecules/LexicalEditor/features/action/components/FlowTransition';
import { useAppSelector } from 'hooks/reduxHooks';
import useProjectRoute from 'hooks/useProjectRoute';
import { STEP_SECTION_COPY } from './FlowStepsPopup.uiCopy';

interface FlowStepsPopupProps {
  onCreate?: AddStepOnChange;
  onCancel?: (reason?: string) => void;
}

interface FlowStepsForm {
  name: string;
}

export const FlowStepsPopup = ({ onCreate, onCancel }: FlowStepsPopupProps) => {
  const { flowId } = useProjectRoute<{
    flowId: string;
  }>();
  const flow = useAppSelector(state =>
    flowsSelectors.selectById(state.flows.flows, flowId),
  );

  const { addStepTransition } = useAddStepFunction(onCreate);

  const {
    register,
    formState: { isValid, errors },
    handleSubmit,
  } = useForm<FlowStepsForm>({
    defaultValues: {
      name: '',
    },
  });

  const validate = (name: string) => {
    if (
      flow?.steps &&
      stepsSelectors.selectAll(flow?.steps).find(s => s.name === name)
    ) {
      return STEP_SECTION_COPY.STEP_NAME_UNIQUE_ERROR;
    }
  };

  const onSubmit: SubmitHandler<FlowStepsForm> = data => {
    if (!isValid) {
      return;
    }
    addStepTransition(data.name);
  };

  return (
    <ConfirmationModal
      buttonText={STEP_SECTION_COPY.ADD_STEP_BUTTON_TEXT}
      cancelText={STEP_SECTION_COPY.CANCEL_BUTTON_TEXT}
      confirmDisabled={!isValid}
      content={
        <Input
          autoComplete="off"
          {...register('name', {
            validate,
          })}
          aria-autocomplete="none"
          data-test-id="step-name-input-btn"
          errors={
            errors.name && errors.name.message ? [errors.name.message] : []
          }
          label={STEP_SECTION_COPY.STEP_NAME_LABEL}
          placeholder={STEP_SECTION_COPY.STEP_NAME_PLACEHOLDER}
          fullWidth
          required
        />
      }
      size="md"
      subtitle={STEP_SECTION_COPY.ADD_STEP_SUBTITLE}
      title={STEP_SECTION_COPY.ADD_STEP_TITLE}
      open
      skipCloseAndConfirm
      onCancelButtonClick={() => onCancel?.()}
      onConfirmButtonClick={handleSubmit(onSubmit)}
    />
  );
};
